import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { adminApi } from '@/api';
import {
  Badge,
  Button,
  Icon,
  Checkbox,
  Input,
  Modal,
  SegmentedTabs,
  Select,
  Textarea,
  useToast,
} from '@/components/ui';
import { errorMessage, fieldErrors } from '@/hooks/useApiError';

import { STUDENT_LIMITS, optionalLimit, planCodeFrom, validatePlan } from './planForm';
import { BILLING_CURRENCIES } from './money';

/**
 * Create or edit a billing plan.
 *
 * Prices are entered in whole currency units because that is how a price is
 * discussed, and converted to the integer minor units the API stores on the
 * way out -- the conversion lives here so no other screen has to remember it.
 *
 * A blank limit means unlimited, which is why the two limit fields are strings
 * in state rather than numbers: `0` is a real, different answer ("none at
 * all") and must survive being typed.
 *
 * A plan is sold to one audience, chosen when it is created and fixed after:
 * a company plan grants hiring capacity, a student plan monthly allowances.
 * The form shows only the fields that audience's plan enforces.
 */

/** The ISO codes offered for a new plan. Students are billed in rupees. */
const CURRENCIES = BILLING_CURRENCIES;

const EMPTY = {
  audience: 'COMPANY',
  name: '',
  description: '',
  price: '',
  currency: 'EUR',
  interval: 'MONTHLY',
  max_active_internships: '',
  max_team_members: '',
  analytics_history_days: '30',
  candidate_sourcing: false,
  ats_integration: false,
  is_public: true,
  is_active: true,
  sort_order: '0',
  // Student allowances: a string (blank until typed) plus an "Unlimited" box.
  ai_cv_reviews_per_month: '3',
  ai_cv_reviews_per_month_unlimited: false,
  ai_roadmaps_per_month: '3',
  ai_roadmaps_per_month_unlimited: false,
  applications_per_month: '10',
  applications_per_month_unlimited: false,
  application_boost: false,
};

function toForm(plan) {
  if (!plan) return EMPTY;
  const allowances = plan.student_entitlements ?? {};
  const limit = (key) => ({
    [key]: allowances[key] == null ? '' : String(allowances[key]),
    [`${key}_unlimited`]: plan.audience === 'STUDENT' && allowances[key] == null,
  });
  return {
    ...EMPTY,
    audience: plan.audience ?? 'COMPANY',
    ...limit('ai_cv_reviews_per_month'),
    ...limit('ai_roadmaps_per_month'),
    ...limit('applications_per_month'),
    application_boost: Boolean(allowances.application_boost),
    name: plan.name,
    description: plan.description ?? '',
    price: String((plan.price_cents ?? 0) / 100),
    currency: plan.currency ?? 'EUR',
    interval: plan.interval ?? 'MONTHLY',
    max_active_internships:
      plan.entitlements?.max_active_internships == null
        ? ''
        : String(plan.entitlements.max_active_internships),
    max_team_members:
      plan.entitlements?.max_team_members == null ? '' : String(plan.entitlements.max_team_members),
    analytics_history_days: String(plan.entitlements?.analytics_history_days ?? 30),
    candidate_sourcing: Boolean(plan.entitlements?.candidate_sourcing),
    ats_integration: Boolean(plan.entitlements?.ats_integration),
    is_public: plan.is_public ?? true,
    is_active: plan.is_active ?? true,
    sort_order: String(plan.sort_order ?? 0),
  };
}

/** The server names `price_cents`; the form shows `price`. */
const SERVER_FIELDS = { price_cents: 'price' };

/** A student allowance for the API: `null` when "Unlimited" is ticked. */
function studentLimit(form, key) {
  return form[`${key}_unlimited`] ? null : Number(form[key]);
}

export function PlanFormModal({ open, plan, onClose }) {
  const isEdit = Boolean(plan);
  /** The seeded free plans are the only ones that may cost nothing. */
  const isFreePlan = isEdit && ['free', 'student-free'].includes(plan.code);

  const queryClient = useQueryClient();
  const toast = useToast();

  const [form, setForm] = useState(() => toForm(plan));
  const [errors, setErrors] = useState({});
  const isStudent = form.audience === 'STUDENT';

  // Reopening for a different plan must not show the previous one's values.
  useEffect(() => {
    if (open) {
      setForm(toForm(plan));
      setErrors({});
    }
  }, [open, plan]);

  /** Switching audience on a new plan also switches the sensible currency. */
  function chooseAudience(audience) {
    setForm((previous) => ({
      ...previous,
      audience,
      currency:
        audience === 'STUDENT' ? 'INR' : previous.currency === 'INR' ? 'EUR' : previous.currency,
    }));
    setErrors({});
  }

  const set = (key) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((previous) => ({ ...previous, [key]: value }));
    // An edited field's error goes away; the rest stay until the next save.
    setErrors((previous) => (previous[key] ? { ...previous, [key]: undefined } : previous));
  };

  const save = useMutation({
    mutationFn: () => {
      const common = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        price_cents: Math.round(Number(form.price) * 100),
        currency: form.currency.trim().toUpperCase(),
        interval: form.interval,
        is_public: form.is_public,
        is_active: form.is_active,
        sort_order: Number(form.sort_order),
      };
      const grants = isStudent
        ? {
            ai_cv_reviews_per_month: studentLimit(form, 'ai_cv_reviews_per_month'),
            ai_roadmaps_per_month: studentLimit(form, 'ai_roadmaps_per_month'),
            applications_per_month: studentLimit(form, 'applications_per_month'),
            application_boost: form.application_boost,
          }
        : {
            max_active_internships: optionalLimit(form.max_active_internships),
            max_team_members: optionalLimit(form.max_team_members),
            analytics_history_days: Number(form.analytics_history_days),
            candidate_sourcing: form.candidate_sourcing,
            ats_integration: form.ats_integration,
          };
      const body = { ...common, ...grants };

      if (isEdit) {
        // The API treats a missing limit as "leave alone", so clearing one
        // back to unlimited has to be asked for explicitly.
        if (isStudent) {
          // One switch clears all three allowances, so the ones still limited
          // are sent again after it.
          const anyUnlimited = STUDENT_LIMITS.some(({ key }) => grants[key] === null);
          const limited = Object.fromEntries(
            STUDENT_LIMITS.filter(({ key }) => grants[key] !== null).map(({ key }) => [
              key,
              grants[key],
            ]),
          );
          return adminApi.updateBillingPlan(plan.code, {
            ...common,
            application_boost: grants.application_boost,
            ...limited,
            clear_student_limits: anyUnlimited,
          });
        }
        return adminApi.updateBillingPlan(plan.code, {
          ...body,
          clear_internship_limit: body.max_active_internships === null,
          clear_team_limit: body.max_team_members === null,
        });
      }
      return adminApi.createBillingPlan({ ...body, audience: form.audience });
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Plan updated' : 'Plan created');
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      queryClient.invalidateQueries({ queryKey: ['admin-plan'] });
      queryClient.invalidateQueries({ queryKey: ['admin-billing-overview'] });
      // The public pricing page reads the same catalogue.
      queryClient.invalidateQueries({ queryKey: ['public-plans'] });
      onClose();
    },
    onError: (error) => {
      // A refusal names its field; show it there rather than in a toast.
      const byField = Object.fromEntries(
        Object.entries(fieldErrors(error)).map(([key, message]) => [
          SERVER_FIELDS[key] ?? key,
          message,
        ]),
      );
      if (Object.keys(byField).length > 0) setErrors(byField);
      else toast.error(errorMessage(error, 'Could not save the plan'));
    },
  });

  function submit(event) {
    event.preventDefault();
    const found = validatePlan(form, { freeAllowed: isFreePlan });
    setErrors(found);
    if (Object.keys(found).length === 0) save.mutate();
  }

  return (
    <Modal
      open={open}
      title={isEdit ? `Edit ${plan.name}` : 'New plan'}
      description={
        isEdit
          ? 'Changes apply to new subscribers. Existing subscribers keep the price, currency, billing period and limits they bought, including at renewal.'
          : 'Companies or students can subscribe as soon as it is saved, unless you hide it below.'
      }
      onClose={onClose}
      size="lg"
    >
      <form onSubmit={submit} noValidate className="divide-line divide-y">
        <Section title="Sold to">
          {isEdit ? (
            <p className="text-ink-muted flex items-center gap-2 text-sm">
              <Badge tone={isStudent ? 'brand' : 'neutral'}>
                {isStudent ? 'Students' : 'Companies'}
              </Badge>
              Fixed once the plan exists.
            </p>
          ) : (
            <SegmentedTabs
              variant="plain"
              label="Who the plan is for"
              tabs={[
                { key: 'COMPANY', label: 'Companies' },
                { key: 'STUDENT', label: 'Students' },
              ]}
              value={form.audience}
              onChange={chooseAudience}
            />
          )}
        </Section>

        <Section title="Plan details">
          <div className="space-y-4">
            <div>
              <Input
                label="Name"
                value={form.name}
                onChange={set('name')}
                error={errors.name}
                required
                maxLength={80}
                placeholder={isStudent ? 'Student Plus' : 'Growth'}
              />
              {/* The code is made from the name, so it is shown, not asked for. */}
              <p className="text-ink-muted mt-1 flex items-center gap-1.5 text-xs">
                <Icon name="tag" size={12} />
                Plan code{' '}
                <code className="bg-surface-muted text-ink rounded px-1.5 py-px font-mono text-[11px]">
                  {isEdit ? plan.code : planCodeFrom(form.name) || '—'}
                </code>
                {isEdit ? 'fixed once the plan exists' : 'made from the name'}
              </p>
            </div>
            <Textarea
              label="Description"
              value={form.description}
              onChange={set('description')}
              error={errors.description}
              rows={2}
              maxLength={2000}
              hint="Shown on the pricing page under the plan's name."
              placeholder={
                isStudent
                  ? 'More AI help and applications for a busy term.'
                  : 'For teams hiring several interns a term.'
              }
            />
          </div>
        </Section>

        <Section title="Pricing">
          <div className="grid gap-4 sm:grid-cols-3">
            <Input
              label="Price"
              required
              inputMode="decimal"
              value={form.price}
              onChange={set('price')}
              error={errors.price}
              hint={
                isFreePlan
                  ? 'The free plan costs nothing.'
                  : `Per ${form.interval === 'YEARLY' ? 'year' : 'month'} · must be above 0`
              }
            />
            <Select
              label="Currency"
              value={form.currency}
              onChange={set('currency')}
              hint={
                isEdit
                  ? 'For new subscribers only.'
                  : isStudent
                    ? 'Students pay in INR.'
                    : undefined
              }
            >
              {/* An existing plan may carry a code outside the short list. */}
              {(CURRENCIES.includes(form.currency)
                ? CURRENCIES
                : [form.currency, ...CURRENCIES]
              ).map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Select>
            <Select label="Billing period" value={form.interval} onChange={set('interval')}>
              <option value="MONTHLY">Monthly</option>
              <option value="YEARLY">Yearly</option>
            </Select>
          </div>
        </Section>

        {isStudent ? (
          <Section
            title="What the plan grants"
            description="Allowances reset on the first of each month."
          >
            <div className="grid gap-4 sm:grid-cols-3">
              {STUDENT_LIMITS.map(({ key, label }) => (
                <div key={key} className="space-y-2">
                  <Input
                    label={`${label} a month`}
                    inputMode="numeric"
                    value={form[`${key}_unlimited`] ? '' : form[key]}
                    onChange={set(key)}
                    error={errors[key]}
                    disabled={form[`${key}_unlimited`]}
                    placeholder={form[`${key}_unlimited`] ? 'Unlimited' : undefined}
                  />
                  <Checkbox
                    label="Unlimited"
                    checked={form[`${key}_unlimited`]}
                    onChange={set(`${key}_unlimited`)}
                  />
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Checkbox
                label="Premium badge — the student's applications are marked Premium to companies"
                checked={form.application_boost}
                onChange={set('application_boost')}
              />
            </div>
          </Section>
        ) : (
          <Section title="What the plan grants" description="Leave a limit blank for unlimited.">
            <div className="grid gap-4 sm:grid-cols-3">
              <Input
                label="Live internships"
                inputMode="numeric"
                value={form.max_active_internships}
                onChange={set('max_active_internships')}
                error={errors.max_active_internships}
                placeholder="Unlimited"
                hint="Published at the same time"
              />
              <Input
                label="Team members"
                inputMode="numeric"
                value={form.max_team_members}
                onChange={set('max_team_members')}
                error={errors.max_team_members}
                placeholder="Unlimited"
                hint="At least 1"
              />
              <Input
                label="Analytics history"
                required
                inputMode="numeric"
                value={form.analytics_history_days}
                onChange={set('analytics_history_days')}
                error={errors.analytics_history_days}
                hint="Days, 1 to 3,650"
              />
            </div>
            <div className="mt-4 space-y-2.5">
              <Checkbox
                label="Candidate sourcing — search students who have not applied"
                checked={form.candidate_sourcing}
                onChange={set('candidate_sourcing')}
              />
              <Checkbox
                label="ATS integration — candidates sent to a connected ATS automatically"
                checked={form.ats_integration}
                onChange={set('ats_integration')}
              />
            </div>
          </Section>
        )}

        <Section title="Visibility">
          <div className="grid gap-4 sm:grid-cols-[1fr_160px]">
            <div className="space-y-2.5">
              <Checkbox
                label="Show on the pricing page"
                checked={form.is_public}
                onChange={set('is_public')}
              />
              <Checkbox
                label="Open to new subscribers"
                checked={form.is_active}
                onChange={set('is_active')}
              />
              <p className="text-ink-muted text-xs">
                {form.is_active && form.is_public
                  ? 'On sale as soon as it is saved.'
                  : !form.is_active
                    ? 'Not on sale: nobody new can subscribe. Current subscribers keep it.'
                    : 'Hidden from the pricing page; an admin can still assign it.'}
              </p>
            </div>
            <Input
              label="Order"
              inputMode="numeric"
              value={form.sort_order}
              onChange={set('sort_order')}
              error={errors.sort_order}
              hint="Lowest first, 0 to 1,000"
            />
          </div>
        </Section>

        <div className="flex justify-end gap-2 pt-5">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={save.isPending}>
            {isEdit ? 'Save changes' : 'Create plan'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** One titled block of the form, separated from the next by a hairline. */
function Section({ title, description, children }) {
  return (
    <section className="py-5 first:pt-0">
      <div className="mb-3">
        <h3 className="text-ink text-[13px] font-semibold">{title}</h3>
        {description && <p className="text-ink-muted mt-0.5 text-xs">{description}</p>}
      </div>
      {children}
    </section>
  );
}
